import { mergeGeometries } from './ConsolidationCommon';
import { InstanceBufferBuilder } from './InstanceBufferBuilder';
import THREE from "three";
import { MeshFlags } from "../MeshFlags";
import { MATERIAL_VARIANT } from "../../render/MaterialManager";
import { ConsolidatedRenderBatch } from '../RenderBatch';
import { StageNames } from '../out-of-core-tile-manager/tasks/defaultStageConfiguration';
import { RenderFlags } from "../RenderFlags";

/** @import { ModelIteratorBVH } from "../ModelIteratorBVH" */
/** @import { Consolidation } from "./Consolidation" */
/** @import { MaterialManager } from ""../../render/MaterialManager" */

/**
 * NodeConsolidationIterator is used by RenderModel to replace fragments of a BVH Node by optimized representations.
 *
 * Instead of rendering each fragment individually, these optimized representations use consolidation and hardware
 * instancing to batch many fragments together to reduce the number of draw calls and improve rendering performance.
 *
 * We try to minimize CPU overhead by avoiding per fragment operations each frame. Culling is done beforehand on the BVH level
 * and per fragment rendering customizations like visibility changes/ghosting, theming and regular selection are evaluated in
 * the shader.
 *
 */
export class NodeConsolidationIterator {
  /**
   * @param {FragmentList}  fragList - Original fragment list
   * @param {ModelIteratorBVH} bvhModelIterator - The BVH model iterator.
   * @param {MaterialManager} materialManager - The material manager
   * @param {Consolidation[]} [consolidations] - Array mapping bvh node ids to per node consolidations.
   */
  constructor(fragList, bvhModelIterator, materialManager, consolidations = []) {
    this._bvhModelIterator = bvhModelIterator;
    this._frags = fragList;
    this._matman = materialManager;
    this._consolidations = consolidations;
    this._consolidationDisabled = false;
    this._consolidatedRenderBatchCache = [];
    this._tempMatrix = new THREE.Matrix4();
    this._tempBox = new THREE.Box3();

    // Out of Core Tile Manager
    this._bvhModelIterator.getOutOfCoreTileManager()?.activateIterator(
      this._bvhModelIterator.getIteratorOutOfCoreManagerId(),
      this._bvhModelIterator
    );
  }

  /**
   * Apply optional model matrix.
   * Note:
   * - We assume here that the consolidation was computed using the original fragment matrices without considering a model transform.
   * - We cannot set the modelTransform on the meshes themselves: Reason is that single-fragment meshes still have the transform of the original fragment
   * @param {ConsolidatedRenderBatch} consolidatedRenderBatch
   */
  applyModelMatrix(consolidatedRenderBatch) {
    const modelMatrix = this._frags.matrix;
    const scene = consolidatedRenderBatch.consolidatedScene;
    if (modelMatrix) {
      scene.matrixWorld.copy(modelMatrix);
      scene.matrixAutoUpdate = false; // Avoid worldMatrix from being recomputed by THREE based on pos/rotation/scale
    } else {
      scene.matrixWorld.identity();
    }

    const consolidation = this.getConsolidation(consolidatedRenderBatch.nodeIndex);
    if (consolidation) {
      const meshes = consolidation.meshes;
      for (let i = 0; i < meshes.length; i++) {
        meshes[i].matrixWorldNeedsUpdate = true;
      }
    }
  }

  /**
   * Get next scene from cache or create a new one.
   * @param {RenderBatch} renderBatch
   * @returns {ConsolidatedRenderBatch}
   */
  acquireConsolidatedRenderBatch(renderBatch) {
    const index = renderBatch.nodeIndex;

    // create new scene on first use
    if (!this._consolidatedRenderBatchCache[index]) {
      this._consolidatedRenderBatchCache[index] = new ConsolidatedRenderBatch(this, renderBatch, this.getConsolidation(index));
      this.applyModelMatrix(this._consolidatedRenderBatchCache[index]);
    }

    const scene = this._consolidatedRenderBatchCache[index].consolidatedScene;

    // We need to clear the scene here because the meshes are reused for multiple renderers and cannot be shared.
    while (scene.children.length > 0) {
      scene.remove(scene.children[scene.children.length - 1]);
    }
    return this._consolidatedRenderBatchCache[index];
  }

  /**
   * Called when the model matrix has changed.
   */
  modelMatrixChanged() {
    for (let i = 0; i < this._consolidatedRenderBatchCache.length; i++) {
      // Note that _consolidatedRenderBatchCache may contain null elements, because elements are indexed by BVH nodeIdx
      const scene = this._consolidatedRenderBatchCache[i];
      if (scene) {
        this.applyModelMatrix(scene);
      }
    }
  }

  /**
   * Get consolidation for a node or for all nodes.
   * @param {number} nodeId
   * @returns {Consolidation|Consolidation[]|undefined}
   */
  getConsolidation(nodeId) {
    return nodeId !== undefined ? this._consolidations[nodeId] : this._consolidations;
  }

  /**
   * Set consolidation for a node.
   * @param {number} nodeId
   * @param {Consolidation} consolidation
   */
  setConsolidation(nodeId, consolidation) {
    this._consolidations[nodeId] = consolidation;
    // If there was an associated render batch, it needs to be regenerated.
    this._consolidatedRenderBatchCache[nodeId] = null;
  }

  /**
   * Called at the beginning of a scene traversal.
   */
  reset() {
    // Disable consolidation if any fragment was moved or if category highlighting mapping is active
    // (consolidated meshes use a single highlightingCategory uniform per draw call, which can't
    // represent per-fragment categories — fall back to individual fragment rendering instead)
    this._consolidationDisabled = this._frags.movedFragments > 0 || !!this._frags.highlightingCategoryCB;

    this._bvhModelIterator.getOutOfCoreTileManager()?.resetLockedTiles(this._bvhModelIterator.getIteratorOutOfCoreManagerId());
  }

  /**
   * Dispose all consolidations.
   */
  dispose() {
    this._consolidations.forEach((cons) => cons?.dispose());
  }

  /**
   * Given a RenderBatch that would normally be rendered next, this function
   * returns an optimized ConsolidatedRenderBatch to replace it.
   *
   * @param {RenderBatch} renderBatch
   * @param {FrustumIntersector} frustum (unused, here for compatibility)
   * @param {number} drawMode
   * @returns {ConsolidatedRenderBatch|RenderBatch} If fragments must be rendered individually, the input RenderBatch
   *           is returned, otherwise the optimized ConsolidatedRenderBatch is returned
   *
   *  TODO: Rename once we get rid of the old ConsolidationIterator
   */
  consolidateNextBatch(renderBatch, frustum, drawMode) {
    // get bvh node index associated with this RenderBatch. We need this to make sure that
    // a RenderBatch is always replaced by the same THREE.Scene object.
    const nodeIndex = renderBatch.nodeIndex;
    const bvhNode = this._bvhModelIterator.getOutOfCoreTileManager()?.getBvhNode(nodeIndex);
    if (!bvhNode) {
      return renderBatch;
    }
    bvhNode.updateLastRendered(
      this._bvhModelIterator.getIteratorOutOfCoreManagerId(),
      this._bvhModelIterator.getOutOfCoreTileManager()?.getFrameCount(this._bvhModelIterator.getIteratorOutOfCoreManagerId())
    );

    // Fallback: Just use original fragments if no optimized meshes are available
    if (this._consolidationDisabled || bvhNode.transparent || !bvhNode.isStageCompleted(StageNames.OPTIMIZED_ON_GPU)) {
      return renderBatch;
    }

    let consolidatedRenderBatch = this.acquireConsolidatedRenderBatch(renderBatch);

    // Consolidated and instanced meshes are still THREE.Mesh objects and need special treatment.
    // The remaining single fragments are contained in consolidatedRenderBatch.
    const scene = consolidatedRenderBatch.consolidatedScene;
    scene.numFragsStreamingDraw = 0;
    const consolidation = this.getConsolidation(nodeIndex);

    // This should never happen (since it implies that this node got executed even though not all geometries have been loaded) and is only added as a safeguard.
    for (const mesh of consolidation.meshes) {
      if (!('addEventListener' in mesh.geometry)) {
        return renderBatch;
      }
    }

    if (consolidation.meshes.length > 0) {
      scene.add(...consolidation.meshes);
    }
    for (let i = 0; i < consolidation.meshes.length; i++) {
      const mesh = consolidation.meshes[i];
      const geom = mesh && mesh.geometry;
      const isLines = geom && (geom.isLines || geom.isWideLines);
      const isPoints = geom && geom.isPoints;
      const isHiddenPrimitive = this._frags.linesHidden && isLines || this._frags.pointsHidden && isPoints;

      // Only render meshes that are not deactivated via the settings or are completely irrelevant for the current draw mode
      mesh.visible = !(isHiddenPrimitive || drawMode === RenderFlags.RENDER_HIDDEN && mesh.allFragsVisible || drawMode !== RenderFlags.RENDER_HIDDEN && mesh.noFragsVisible);

      if (geom?.streamingDraw)
      scene.numFragsStreamingDraw++;
    }

    this._bvhModelIterator.getOutOfCoreTileManager()?.lockTile(this._bvhModelIterator.getIteratorOutOfCoreManagerId(), nodeIndex);

    // Update bounding box of the scene
    if (!scene.boundingBox) {
      scene.boundingBox = new THREE.Box3();
    }

    // Note: The model matrix is included in the renderBatch bbox already. It is not affected by the scene.matrix.
    renderBatch.getBoundingBox(scene.boundingBox);

    consolidatedRenderBatch.renderImportance = renderBatch.renderImportance;

    // adopt sortObjects flag from original RenderBatch - so that RenderScene can use it to detect which
    // scenes contain transparency.
    scene.sortObjects = renderBatch.sortObjects;

    if (renderBatch.lights) {
      scene.children.push(renderBatch.lights);
    }

    return consolidatedRenderBatch;
  }

  /**
   * Updates a given render proxy mesh to make sure that it matches exactly with the fragment's representation
   * used in the last rendered frame.
   *
   * @param {THREE.Mesh} proxy  - currently used render proxy
   * @param {number} fragId - fragment represented by this proxy
   */
  updateRenderProxy(proxy, fragId) {
    // if the proxy has no valid geometry, do nothing
    if (!proxy.geometry || !proxy.geometry.attributes || !this._bvhModelIterator.getOutOfCoreTileManager()) {
      return;
    }

    // check which type of geometry has been used in last rendering traversal (See ConsolidationType enum)
    const requiredType = this.getFragmentConsolidationType(fragId);
    const currentType = NodeConsolidationIterator.getConsolidationType(proxy.geometry);

    // if type is already correct, we are done.
    if (!proxy.needsUpdate && currentType == requiredType) {
      return;
    }

    // get original fragment geometry
    const origGeom = this._frags.getGeometry(fragId);
    if (requiredType === NodeConsolidationIterator.ConsolidationType.Original) {

      // recover original geometry, material, and matrix
      proxy.geometry = origGeom;
      proxy.material = this._frags.getMaterial(fragId);
      this._frags.getWorldMatrix(fragId, proxy.matrix);
    } else if (requiredType === NodeConsolidationIterator.ConsolidationType.Instanced) {
      // This fragment was rendered using an instanced shape.

      // replace proxy geometry by instanced mesh with single instance
      this._frags.getWorldMatrix(fragId, this._tempMatrix);
      const dbId = this._frags.fragments.fragId2dbId[fragId];
      // create proxy mesh with 1-element instance buffer
      const builder = new InstanceBufferBuilder(origGeom, 1);
      builder.addInstance(this._tempMatrix, dbId);
      proxy.geometry = builder.finish();

      // use container material (needed to activate instancing)
      proxy.material = this._matman.getMaterialVariant(
        this._frags.getMaterial(fragId),
        MATERIAL_VARIANT.INSTANCED,
        this._bvhModelIterator.getOutOfCoreTileManager().model
      );

      // reset matrix to identity, because the transform is done per instance
      proxy.matrix.identity();
    } else {// ConsolidationType.Merged:
      // This fragment was rendered using a merged shape

      // create consolidation proxy which just contains the single fragment with baked matrix
      this._frags.getWorldMatrix(fragId, this._tempMatrix);
      this._frags.getWorldBounds(fragId, this._tempBox);
      const dbId = this._frags.fragments.fragId2dbId[fragId];
      proxy.geometry = mergeGeometries([origGeom], this._tempMatrix.elements, [dbId], this._tempBox);

      // share container material
      proxy.material = this._matman.getMaterialVariant(
        this._frags.getMaterial(fragId),
        MATERIAL_VARIANT.VERTEX_IDS,
        this._bvhModelIterator.getOutOfCoreTileManager().model
      );

      // reset matrix to identity, because the transform is baked into the vertex buffer
      proxy.matrix.identity();
    }
    // Make sure we don't create the proxy mesh again until actually needed.
    proxy.needsUpdate = false;

    // make sure that WebGLRenderer does not keep an outdated cache object. Without this line,
    // WebGLRenderer will still use the previous GeometryBuffer if it is already cached.
    proxy.dispatchEvent({ type: 'removed' });
  }

  /**
   * Creates a copy of the iterator
   * @param {ModelIteratorBVH} bvhModelIterator - The new BVH model iterator. Since we are cloning
   * the bvh model iterator and the consolidation iterator independent from each other, we have to
   * provide the new BVH model iterator to the cloned consolidation iterator to make sure that the
   * cloned consolidation iterator references the new BVH model iterator.
   * @returns {NodeConsolidationIterator} Cloned iterator
   */
  clone(bvhModelIterator) {
    return new NodeConsolidationIterator(this._frags, bvhModelIterator, this._matman, this._consolidations);
  }

  /**
   * Called when a render batch is complete.
   * @param {RenderBatch} renderBatch
   */
  onRenderBatchComplete(renderBatch) {
    // Mark the node as no longer locked in the tile manager, once it has been rendered and
    // the render batch that references consolidated geometry from the tile will no longer
    if (renderBatch.nodeIndex !== undefined) {
      this._bvhModelIterator.getOutOfCoreTileManager()?.unlockTile(this._bvhModelIterator.getIteratorOutOfCoreManagerId(), renderBatch.nodeIndex);
    }
  }

  /**
   * Checks if a given geometry is instanced, the result of merging, or original fragment geometry.
   * @param {THREE.BufferGeometry} geom
   * @returns {number} ConsolidationType
   */
  static getConsolidationType(geom) {
    if (geom) {
      if (geom.numInstances) {
        // This geom combines multiple fragments using instancing
        // Note that we also enter this section if numInstances==1. This is correct, because numInstances
        // is always undef if no instance buffer is used.
        return NodeConsolidationIterator.ConsolidationType.Instanced;

        // It is possible that a geometry is a consolidation placeholder that has no attributes yet due to
        // the limited time budget for consolidation. Since it will eventually be consolidated, we treat it as
        // a merged geometry.
      } else if (geom.attributes) {
        // When merging fragments, we always use per-vertex ids.
        return NodeConsolidationIterator.ConsolidationType.Merged;
      }
    }

    return NodeConsolidationIterator.ConsolidationType.Original;
  }

  /**
   * Checks which type of consolidation has been used to represent a given fragment in the last rendering traversal.
   * @param {number} fragId
   * @returns {number} ConsolidationType
   */
  getFragmentConsolidationType(fragId) {
    let consolidationType = NodeConsolidationIterator.ConsolidationType.Original;
    // Check if consolidation was used for this fragment in last frame.
    if (!this._consolidationDisabled) {
      if (this._frags.isFlagSet(fragId, MeshFlags.MESH_CONSOLIDATED))
      consolidationType = NodeConsolidationIterator.ConsolidationType.Merged;else
      if (this._frags.isFlagSet(fragId, MeshFlags.MESH_INSTANCED))
      consolidationType = NodeConsolidationIterator.ConsolidationType.Instanced;
    }
    return consolidationType;
  }
}

/**
 * Enum to describe in which way a fragment has been rendered.
 * @readonly
 * @enum {number}
 */
NodeConsolidationIterator.ConsolidationType = {
  Merged: 1, // Fragment is represented by a merged geometry composed from different fragment geometries.
  Instanced: 2, // Fragment is represented by an instanced shape that represents multiple fragments that are sharing the same geometry.
  Original: 3 // Fragment was not combined with others and is still sharing the original fragment's geometry and material.
};